import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { IMAGE_BASE } from "../Config";
import { fetchLatestContentByType } from "../services/ContentService";
import { getCachedLatestNews } from "../utils/homePageCache";

interface LatestNewsSectionProps {
  news?: any[];
  useCached?: boolean;
}

const LatestNewsSection: React.FC<LatestNewsSectionProps> = ({ news: propNews, useCached = false }) => {
  const [news, setNews] = useState<any[]>(propNews || []);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // If news were provided as props, use those instead of fetching
    if (propNews && propNews.length > 0) {
      setNews(propNews);
      setLoading(false);
      return;
    }
    
    const fetchNews = async () => {
      try {
        setLoading(true);
        let data;
        if (useCached) {
          data = await getCachedLatestNews(3);
        } else {
          data = await fetchLatestContentByType('news', 3);
          // Store in cache for future use
          const { cacheSet } = await import('../utils/cacheUtil');
          await cacheSet(`latest_news_3`, data, 21600); // 6 hours TTL
        }
        setNews(data);
      } catch (error) {
        console.error('Error fetching news:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchNews();
  }, [propNews]);
  
  // Check if we have news
  const hasNews = news && news.length > 0;

  // Use all news items (already limited to 3 in the API call)
  const latestNews = news;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section className="py-5" style={{ backgroundColor: "#fff" }}>
      <Container>
        <div className="text-center mb-4">
          <h2 className="mb-2" style={{ color: "#FF8F00" }}>Latest News</h2>
          <p className="text-muted">Stay informed with the latest updates</p>
          <div 
            className="mx-auto mb-4" 
            style={{ 
              width: "60px", 
              height: "3px", 
              background: "linear-gradient(45deg, #FF8F00, #FFC107)", 
              borderRadius: "2px" 
            }}
          ></div>
        </div>

        <Row className="g-4">
          {latestNews.map((item) => (
            <Col key={item._id} md={4}>
              <Card 
                className="h-100 border-0 shadow-sm" 
                style={{ 
                  borderRadius: '15px',
                  transition: "all 0.3s ease",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 10px 20px rgba(255, 143, 0, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                }}
              >
                <div style={{ position: "relative", height: "180px", overflow: "hidden" }}>
                  {item.imageLink ? (
                    <Card.Img 
                      variant="top" 
                      src={IMAGE_BASE + item.imageLink} 
                      style={{ height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ 
                        height: "100%", 
                        background: "linear-gradient(135deg, #FFC107 0%, #FF8F00 100%)" 
                      }}
                    >
                      <i className="bi bi-newspaper text-white" style={{ fontSize: "2rem" }}></i>
                    </div>
                  )}
                  <div 
                    style={{ 
                      position: "absolute", 
                      top: "10px", 
                      right: "10px",
                      background: "rgba(255,255,255,0.9)",
                      padding: "5px 10px",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      color: "#FF8F00"
                    }}
                  >
                    <i className="bi bi-calendar2 me-1"></i>
                    {formatDate(item.createdAt)}
                  </div>
                </div>
                <Card.Body>
                  <Card.Title style={{ fontSize: "1.1rem", fontWeight: "600", color: "#FF8F00" }}>
                    {item.title}
                  </Card.Title>
                  <Card.Text className="text-muted" style={{ fontSize: "0.9rem" }}>
                    {item.content && item.content.length > 100 
                      ? `${item.content.substring(0, 100).replace(/<[^>]*>/g, '')}...` 
                      : item.content?.replace(/<[^>]*>/g, '')}
                  </Card.Text>
                  <Link 
                    to={`/article/${item._id}`} 
                    className="btn btn-sm mt-2"
                    style={{ 
                      backgroundColor: "#FFC107", 
                      color: "#212121", 
                      borderRadius: "20px",
                      padding: "5px 15px",
                      fontWeight: "500"
                    }}
                  >
                    Read More <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <div className="text-center mt-4">
          <Link 
            to="/news" 
            className="btn px-4 py-2"
            style={{ 
              backgroundColor: "#FF8F00", 
              color: "white", 
              borderRadius: "30px",
              fontWeight: "500"
            }}
          >
            View All News <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default LatestNewsSection;